import ApiService from './api.service';

export interface FileRecord {
  id: string;
  name: string;
  size: number;
  mime_type: string;
  uploaded_at: string;
  processing_status: string;
  source_record_type_id?: string;
}

export interface FilePart {
  id: string;
  file_id: string;
  part_identifier: string;
  headers: string[];
  sample_rows: string[][];
  record_count: number;
  processing_status: string;
}

export type ListRecordsResponse<T> = {
  records: T[];
  total_records?: number;
  total_pages?: number;
  page?: number;
};

export class FileService {
  async find(filters: Record<string, any> = {}): Promise<ListRecordsResponse<FileRecord>> {
    const queryParams: Record<string, any> = {
      page: 1,
      limit: 500,
    };

    if (Object.keys(filters).length > 0) {
      queryParams.filter = JSON.stringify(filters);
    }

    const result = await ApiService.getInstance().request(
      'GET',
      '/files',
      queryParams
    ) as ListRecordsResponse<FileRecord>;

    return result;
  }

  async downloadOriginal(fileId: string): Promise<void> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/files/${fileId}/original-file`
    ) as { location_url: string };
    window.open(result.location_url, '_blank');
  }

  async process(fileId: string): Promise<{ job_id: string }> {
    return await ApiService.getInstance().request(
      'POST',
      `/files/${fileId}/process`
    ) as { job_id: string };
  }

  async getParts(fileId: string): Promise<ListRecordsResponse<FilePart>> {
    return await ApiService.getInstance().request(
      'GET',
      `/files/${fileId}/parts`
    ) as ListRecordsResponse<FilePart>;
  }

  async loadPart(
    fileId: string,
    partId: string,
    sourceRecordTypeId: string,
    columns: string[],
    pipelineNodeId?: string
  ): Promise<{ job_id: string }> {
    return await ApiService.getInstance().request(
      'POST',
      `/files/${fileId}/parts/${partId}/load`,
      {
        source_record_type_id: sourceRecordTypeId,
        columns,
        pipeline_node_id: pipelineNodeId,
      }
    ) as { job_id: string };
  }

  async createSourceRecordType(opts: {
    name: string;
    sourceId: string;
    columns: string[];
  }): Promise<{ id: string }> {
    const result = await ApiService.getInstance().request(
      'POST',
      '/sourcerecordtypes',
      {
        source_id: opts.sourceId,
        name: opts.name,
        description: '',
        resource_name: '',
        columns: opts.columns.map(col => ({
          key: col,
          label: col,
          is_system_field: false,
          is_synthetic_field: false,
        })),
        composite_key: [],
        column_preferences: [],
      }
    ) as { record: { id: string } };
    return { id: result.record.id };
  }

  async deleteFromNode(nodeId: string, fileId: string): Promise<{ ok: boolean; job_id: string }> {
    return await ApiService.getInstance().request(
      'DELETE',
      `/pipelinenodes/${nodeId}/delete-file/${fileId}`
    ) as { ok: boolean; job_id: string };
  }
}

export const fileService = new FileService();
